import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls/web-storage-service';

import {
	type CachedEntry,
	readBrowserStorageCache,
	writeBrowserStorageCache,
} from '../common/browser-storage-cache';
import { isRecord } from '../common/is-record';
import { getGeneratedCacheKey } from './cache-key';
import type { GeneratedOptions, GeneratedRequest } from './types';

export const getGenerateEndpoint = (slug: string): string => `/gateway/api/potd/${slug}/generate`;

interface MemoryEntry {
	expiresAt: number;
	data: unknown;
}

/**
 * Outlives every mount, so remounting does not re-ask and two surfaces mounted together share one
 * answer. It is also what keeps a consent-blocked storage write from costing a request per mount.
 */
const memoryCache = new Map<string, MemoryEntry>();

/** One entry per key while a request is open, so concurrent callers share a single POST. */
const inFlightRequests = new Map<string, Promise<unknown>>();

const requestOutput = async (
	{ slug, args }: GeneratedRequest,
	fetchFn: GeneratedOptions<unknown>['fetchFn'],
): Promise<unknown> => {
	const performFetch = fetchFn ?? globalThis.fetch;
	if (typeof performFetch !== 'function') {
		return null;
	}

	const response = await performFetch(getGenerateEndpoint(slug), {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ args: args ?? {} }),
	});

	const body: unknown = response.ok ? await response.json() : null;
	return isRecord(body) && body.status === 'ok' ? body.output : null;
};

/**
 * Issues the network request and writes whatever comes back into both caches, whatever the
 * outcome, so a repeated read (or a repeated refresh) at most asks once for as long as the request
 * is open. Shared by `fetchGenerated` and `refreshGenerated` — the only difference between the two
 * is whether a cache hit is allowed to short-circuit this.
 */
const requestAndCache = <T>(
	request: GeneratedRequest,
	cacheKey: string,
	{ parse, getExpiresAt, fetchFn }: GeneratedOptions<T>,
): Promise<T | null> => {
	const pendingRequest = requestOutput(request, fetchFn)
		.catch(() => null)
		.then((output) => {
			const data = parse(output);
			const generatedAt = Date.now();
			const expiresAt = getExpiresAt(generatedAt);
			// Recorded whatever the outcome, `null` included, so one page load asks at most once.
			memoryCache.set(cacheKey, { expiresAt, data });

			if (data !== null) {
				// The served `output`, not `data`, so reading another of its fields later needs no new
				// cache version.
				writeBrowserStorageCache(AtlBrowserStorageLocal, cacheKey, {
					expiresAt,
					generatedAt,
					value: output,
				});
			}

			return data;
		})
		.finally(() => {
			inFlightRequests.delete(cacheKey);
		});

	inFlightRequests.set(cacheKey, pendingRequest);
	return pendingRequest;
};

/**
 * Resolves a served slug's parsed output, from memory, browser storage, an already-open request, or
 * a new one, in that order.
 *
 * Never rejects: the surfaces showing this content are optional, so a failure resolves `null` and
 * leaves the host unchanged.
 */
export const fetchGenerated = async <T>(
	request: GeneratedRequest,
	options: GeneratedOptions<T>,
): Promise<T | null> => {
	const cacheKey = getGeneratedCacheKey(request);
	const now = Date.now();

	const memoryEntry = memoryCache.get(cacheKey);
	if (memoryEntry !== undefined && memoryEntry.expiresAt > now) {
		return memoryEntry.data as T | null;
	}

	const storedEntry = readBrowserStorageCache(AtlBrowserStorageLocal, cacheKey, options.parse, now);
	if (storedEntry !== null) {
		memoryCache.set(cacheKey, { expiresAt: storedEntry.expiresAt, data: storedEntry.value });
		return storedEntry.value;
	}

	const openRequest = inFlightRequests.get(cacheKey);
	if (openRequest !== undefined) {
		return openRequest as Promise<T | null>;
	}

	return requestAndCache(request, cacheKey, options);
};

/**
 * Forces a fresh request for this key and overwrites the memory and storage caches with whatever
 * comes back, bypassing a cache hit that `fetchGenerated` would otherwise have served.
 *
 * Joins an already-open request instead of starting a second one — a refresh racing the initial
 * load is answered by the same in-flight POST, which is already asking for current content.
 *
 * Never rejects, matching `fetchGenerated`: the caller is refreshing a cache for next time, not
 * blocking on an answer it needs right now.
 */
export const refreshGenerated = async <T>(
	request: GeneratedRequest,
	options: GeneratedOptions<T>,
): Promise<T | null> => {
	const cacheKey = getGeneratedCacheKey(request);

	const openRequest = inFlightRequests.get(cacheKey);
	if (openRequest !== undefined) {
		return openRequest as Promise<T | null>;
	}

	return requestAndCache(request, cacheKey, options);
};

// Replaces a cached answer in memory and storage without a request, keeping its timestamps so an
// edit never extends its life (e.g. dropping a suggestion once it has been used).
export const replaceGeneratedOutput = <T>(
	request: GeneratedRequest,
	parse: GeneratedOptions<T>['parse'],
	output: unknown,
	timestamps: Pick<CachedEntry<unknown>, 'expiresAt' | 'generatedAt'>,
): void => {
	const cacheKey = getGeneratedCacheKey(request);
	// An expired entry stays out of memory, so it cannot overwrite the record of a request that failed.
	if (timestamps.expiresAt > Date.now()) {
		memoryCache.set(cacheKey, { expiresAt: timestamps.expiresAt, data: parse(output) });
	}
	writeBrowserStorageCache(AtlBrowserStorageLocal, cacheKey, { ...timestamps, value: output });
};

/** Test seam: the caches above otherwise leak between cases. */
export const testing_resetGeneratedCache = (): void => {
	memoryCache.clear();
	inFlightRequests.clear();
};
